import { ProportionalityRuleType } from '@/Network/Types/Responses/ScheduleUnitResponses'
import type { DSAttendancePolicyPayload } from '@/Network/Types/Requests/DsAttendanceRequest'
import type { ScheduleAgendaTypeValue } from '@/Network/Types/Responses/DemandResponses'
import type { CreateApprovalLayerRequest } from '@/Network/Types/Requests/ApprovalFlowRequests'
import type { CreateQualificationDocumentalLinkRequest } from '@/Network/Types/Requests/QualificationDocumentalRequests'
import type { CreateQualificationTransferLinkRequest } from '@/Network/Types/Requests/QualificationTransferRequests'
import type { CreateScheduleQualificationPolicy } from '@/types/qualification-policy.types'

export enum ProportionalityRuleEnum {
  PROPORTIONAL_WITH_LIMIT = 'PROPORTIONAL_WITH_LIMIT',
  PROPORTIONAL_WITHOUT_LIMIT = 'PROPORTIONAL_WITHOUT_LIMIT',
  INTEGRAL = 'INTEGRAL'
}

export type CreateSchedule = {
  name: string
  timezone: string
  endDate?: Date | string | null
  sector?: number
  requiredCheckNotOurTeam?: boolean
  requiredCheckOurTeam?: boolean
  doctorHandoverAuthorizationMinutes: number
  proportionalityRule: ProportionalityRuleType
  requiresDoctorPaymentFormation?: boolean
  operationalGroup?: string | null
  isVisible?: boolean
  isVisibleWeb?: boolean
  isShareable?: boolean
  doctorVisibleFrom?: string | null
  doctorVisibleUntil?: string | null
  allowDoctorStationHandover?: boolean
  requiresAdminApproval?: boolean
  /** Tipo de agenda (Demanda, SPEC 2.2 §4.5). Só enviado com a flag da Demanda. */
  agendaType?: ScheduleAgendaTypeValue
}

export type UpdateSchedule = {
  name?: string
  timezone?: string
  endDate?: Date | string | null
  sector?: number
  requiredCheckNotOurTeam?: boolean
  requiredCheckOurTeam?: boolean
  doctorHandoverAuthorizationMinutes?: number
  proportionalityRule?: ProportionalityRuleType
  requiresDoctorPaymentFormation?: boolean
  operationalGroup?: string | null
  teamDoctorReminderEnabled?: boolean
  externalDoctorReminderEnabled?: boolean
  /** Tipo de agenda (Demanda, SPEC 2.2 §4.5). Só enviado com a flag da Demanda. */
  agendaType?: ScheduleAgendaTypeValue
  isVisible?: boolean
  isVisibleWeb?: boolean
  isShareable?: boolean
  doctorVisibleFrom?: string | null
  doctorVisibleUntil?: string | null
  allowDoctorStationHandover?: boolean
  requiresAdminApproval?: boolean
  handoverRequestHours?: number | null
  handoverRequestTiming?: string | null
  handoverRequestReference?: string | null
  handoverExpirationHours?: number | null
  handoverExpirationTiming?: string | null
  handoverExpirationReference?: string | null
  allowDoctorStationHandoverNotOurTeam?: boolean
  requiresAdminApprovalNotOurTeam?: boolean
  handoverRequestHoursNotOurTeam?: number | null
  handoverRequestTimingNotOurTeam?: string | null
  handoverRequestReferenceNotOurTeam?: string | null
  handoverExpirationHoursNotOurTeam?: number | null
  handoverExpirationTimingNotOurTeam?: string | null
  handoverExpirationReferenceNotOurTeam?: string | null
  limitConsecutiveHours?: boolean
  maxConsecutiveHours?: number | null
  minRestHours?: number | null
  consecutiveHoursOurTeamOnly?: boolean
  limitConsecutiveHoursNotOurTeam?: boolean
  maxConsecutiveHoursNotOurTeam?: number | null
  minRestHoursNotOurTeam?: number | null
  consecutiveHoursOurTeamOnlyNotOurTeam?: boolean
  allowUnregisteredDoctor?: boolean
  /** Só com `allowUnregisteredDoctor=false`: libera os plantões FUTUROS como vaga aberta. */
  releaseFutureUnregistered?: boolean
}

export type FiltersSchedule = {
  sectorIds?: number[]
  healthUnitIds?: number[]
}

/**
 * Filtros da listagem de escalas disponíveis para importação de configuração
 * (`GET /schedules/importable-configurations`).
 *
 * `q` busca em nome de escala, unidade e empresa no servidor. O backend limita
 * `limit` a 50 — pedir mais devolve 400.
 */
export type ListImportableSchedulesParams = {
  q?: string
  /**
   * Restringe às escalas de uma unidade. Usado no modo de navegação do modal, em que
   * a estrutura vem de `listImportableTree()` e cada unidade pagina sozinha. O backend
   * aplica em AND com o escopo de acesso — nunca amplia visibilidade.
   */
  healthUnitId?: number
  offset?: number
  limit?: number
}

export interface CreateFullScheduleRequest {
  schedule: {
    name: string
    timezone: string
    endDate?: Date | string | null
    healthUnitId: number
    proportionalityRule: string
    requiredCheckOurTeam?: boolean // Expand and Contract: Agora é opcional, migrado para a política de registro de plantão (ds-attendance)
    requiredCheckNotOurTeam?: boolean // Expand and Contract: Agora é opcional, migrado para a política de registro de plantão (ds-attendance)
    requiresDoctorPaymentFormation: boolean
    doctorHandoverAuthorizationMinutes?: number
    operationalGroup: string | null
    allowDoctorStationHandover?: boolean
    requiresAdminApproval?: boolean
    handoverRequestHours?: number | null
    handoverRequestTiming?: string | null
    handoverRequestReference?: string | null
    handoverExpirationHours?: number | null
    handoverExpirationTiming?: string | null
    handoverExpirationReference?: string | null
    allowDoctorStationHandoverNotOurTeam?: boolean
    requiresAdminApprovalNotOurTeam?: boolean
    handoverRequestHoursNotOurTeam?: number | null
    handoverRequestTimingNotOurTeam?: string | null
    handoverRequestReferenceNotOurTeam?: string | null
    handoverExpirationHoursNotOurTeam?: number | null
    handoverExpirationTimingNotOurTeam?: string | null
    handoverExpirationReferenceNotOurTeam?: string | null
    limitConsecutiveHours?: boolean
    maxConsecutiveHours?: number | null
    minRestHours?: number | null
    consecutiveHoursOurTeamOnly?: boolean
    limitConsecutiveHoursNotOurTeam?: boolean
    maxConsecutiveHoursNotOurTeam?: number | null
    minRestHoursNotOurTeam?: number | null
    consecutiveHoursOurTeamOnlyNotOurTeam?: boolean
    /** Tipo de agenda (Demanda, SPEC 2.2 §4.5). Só enviado com a flag da Demanda. */
    agendaType?: ScheduleAgendaTypeValue
  }
  positions: { name: string }[]
  shifts: { name: string }[]
  formations: { name: string }[]

  dsAttendancePolicy?: DSAttendancePolicyPayload

  approvalFlow?: {
    name?: string
    layers: CreateApprovalLayerRequest[]
  }

  /**
   * Vínculo de política de qualificação a replicar (RN-16), criado DENTRO da
   * transação do full-create (BUG-PP-06) — antes ia num POST separado pós-create,
   * que sofria 403 intermitente por corrida de propagação de permissão
   * (outbox→SpiceDB). Ausente = a escala de origem não tinha o vínculo.
   */
  qualificationDocumentalPolicy?: CreateQualificationDocumentalLinkRequest
  qualificationTransferPolicy?: CreateQualificationTransferLinkRequest

  paymentPolicy: {
    name: string
    link: {
      startDate: string | null
      endDate: string | null
    }
    rules: {
      weekdays: number[]
      shifts: string[]
      positions: string[]
      isOurTeamValues: string[]
      formations: string[]
      paymentTiming: string
      hourValue: number
    }[]
  }
  /** Qualificação exigida (medical-docs-qualification, A3.6). Só com a flag e com critério. */
  qualificationPolicy?: CreateScheduleQualificationPolicy
}

export interface CreateFullScheduleResponse {
  id: number
  name: string
}

export interface OrderEntry {
  shift_id: number
  position_id: number
}

export interface ScheduleOrderPayload {
  schedule_id: number
  order: OrderEntry[]
}

export type UpdateVisualizationOrderPayload = ScheduleOrderPayload[]
